import { useSnackbar } from "notistack";
import {
  getParticipantOnlinePresence,
  jobMessageList,
  updateMessageSeen,
} from "../endpoints/job.service";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import {
  fetchJobMessagesProfile,
  resetRoomUnread,
  setJobMembersProfile,
} from "../store/actions/message.action";
import { useSocket } from "context/SocketProvider";
import { v4 as uuidv4 } from "uuid";
import { updateAndSortChatGroups } from "@utils/updateAndSortChatGroups";
import store from "@store/index";
import { RootState } from "@store/reducers";
import {
  setOnlineStatus,
  resetOnlineStatus,
} from "../store/actions/job.action";

const LIMIT = 100;

export const MESSAGE_EVENTS = {
  JOIN_ROOM: "chat.join",
  LEAVE_ROOM: "chat.leave",
  SEND_MESSAGE: "job:chat:send",
  RECEIVE_MESSAGE: "job:chat:receive",
  TYPING: "job:chat:typing",
  REFRESH_MESSAGES: "job:chat:refresh",
  PRESENCE: "presence.update",
};

const useChat = ({ jobId, receiverDetails, fetchInbox = false }) => {
  const dispatch = useDispatch();
  const socketRef = useSocket();
  const socket = socketRef?.current;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((state: RootState) => state.auth);

  // Local state
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [isMessageLoading, setIsMessageLoading] = useState(true);
  const [isOlderMessageLoading, setIsOlderMessageLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const onlineStatus = useSelector((state: RootState) => state.job.onlineStatus);

  const recipientUserId = receiverDetails?.userId;

  const appendLocalMessage = (message) => {
    if (!message) return;
    setMessages((prev) => [...prev, message]);
  };

  console.log("useChat", jobId, receiverDetails);

  async function loadOlderMessages() {
    setIsOlderMessageLoading(true);
    jobMessageList({
      jobId,
      receiverId: recipientUserId,
      cursor: nextCursor,
    })
      .then((response) => {
        setHasNextPage(response?.data?.pagination?.hasNextPage);
        setNextCursor(response?.data?.pagination?.nextCursor);
        // setPage((prev) => prev + 1);
        const olderMessages = response?.data?.data?.reverse();
        console.log(
          "check older messages",
          olderMessages,
          response?.data?.messages,
        );
        setMessages((prev) => {
          return [...olderMessages, ...prev];
        });
      })
      .catch((error) => {
        console.error("❌ useChat: Failed to fetch older message list", error);
      })
      .finally(() => {
        setIsOlderMessageLoading(false);
      });
  }

  async function refreshMessages() {
    if (!jobId || !recipientUserId) return;

    setIsMessageLoading(true);
    try {
      const response = await jobMessageList({
        receiverId: recipientUserId,
        jobId,
        limit: LIMIT,
      });
      setHasNextPage(response?.data?.pagination?.hasNextPage);
      setNextCursor(response?.data?.pagination?.nextCursor);
      const formattedData = response?.data?.data;
      setMessages(formattedData?.reverse() || []);
      dispatch(fetchJobMessagesProfile());
    } catch (error) {
      console.error("❌ useChat: Failed to refresh message list", error);
    } finally {
      setIsMessageLoading(false);
    }
  }

  // Send message handler
  async function sendMessage({
    message,
    setMessage,
    attachments = [],
    setAttachments = null,
  }) {
    if (!recipientUserId) return;
    const hasContent = !!message?.trim();
    const hasAttachments = Array.isArray(attachments) && attachments.length > 0;
    if (!hasContent && !hasAttachments) return;
    try {
      setIsMessageSending(true);

      try {
        const chatId = uuidv4();
        const payload = {
          chatId,
          content: message || "",
          jobId,
          recipientId: recipientUserId,
          attachments,
        };
        console.log("send message payload", payload);
        const updatedChatGroups = updateAndSortChatGroups(
          store.getState()?.messageReducer?.membersProfile,
          {
            ...payload,
            senderId: user?._id,
            createdAt: new Date().toString(),
          },
        );
        dispatch(setJobMembersProfile(updatedChatGroups));
        socket.emit(MESSAGE_EVENTS.SEND_MESSAGE, payload);
        // Add temporary message to UI immediately
        setMessages((prev) => {
          return [
            ...prev,
            {
              chatId,
              message: message || "",
              attachments,
              createdName: user?.firstName + " " + user?.lastName,
              seen: false,
              createdBy: user?._id || "",
              createdAt: new Date(),
              isTemp: true,
            },
          ];
        });
        // console.log("Socket send message payload", receiverDetails, payload);
      } catch (error) {
        console.log(error, "logging here error in temp message emit");
      }

      setMessage("");
      if (typeof setAttachments === "function") {
        setAttachments([]);
      }
    } catch (error) {
      enqueueSnackbar("Error sending message: " + error.message, {
        variant: "error",
      });
    } finally {
      setIsMessageSending(false);
    }
  }

  function triggerTyping(status) {
    console.log("Typing triggered");
    const payload = {
      jobId,
      recipientId: recipientUserId,
      typing: status,
    };
    socket.emit(MESSAGE_EVENTS.TYPING, payload);
  }

  // ----------------
  // HANDLE PRESENCE EVENT
  // ----------------
  const handlePresenceEvent = (data: any) => {
    if (data?.userId === receiverDetails?.userId) {
      dispatch(setOnlineStatus(data));
    }
  };

  // JOIN AND LEAVE ROOM
  useEffect(() => {
    if (!socket || !jobId || !recipientUserId) return;
    const payload = {
      jobId,
      participantId: recipientUserId,
    };

    console.log("Joining room:", payload);
    socket.emit(MESSAGE_EVENTS.JOIN_ROOM, payload);
    return () => {
      console.log("Leaving room:", payload);
      socket.emit(MESSAGE_EVENTS.LEAVE_ROOM, payload);
    };
  }, [socket, jobId, recipientUserId]);

  // useEffect(() => {
  //   if (!socket) return;

  //   const handleTempMessage = (msg) => {
  //     const isSameJobId = msg?.jobApplicationId === jobId;
  //     const isCurrentUsersMessage = msg?.createdBy === user?._id;

  //     if (isSameJobId && isCurrentUsersMessage) {
  //       setMessages((prev) => {
  //         return [
  //           ...prev,
  //           {
  //             chatId: msg.chatId,
  //             message: msg.message,
  //             createdName: msg.createdName,
  //             seen: msg.seen,
  //             createdBy: msg.createdBy,
  //             isTemp: msg.isTemp,
  //           },
  //         ];
  //       });
  //     }
  //   };

  //   socket.on("tempMessage", handleTempMessage);

  //   return () => {
  //     socket.off("tempMessage", handleTempMessage);
  //   };
  // }, [socket, jobId]);

  useEffect(() => {
    if (!recipientUserId) return;
    const fetchParticipantOnlinePresence = async () => {
      try {
        const { data: participantOnlinePresence } =
          await getParticipantOnlinePresence(recipientUserId);
        if (
          participantOnlinePresence &&
          participantOnlinePresence.userId === recipientUserId
        ) {
          dispatch(setOnlineStatus(participantOnlinePresence));
        }
      } catch (error) {
        console.error("Error fetching particiapnt online presence", error);
      }
    };
    fetchParticipantOnlinePresence();

    return () => {
      dispatch(resetOnlineStatus());
    };
  }, [recipientUserId, dispatch]);

  // Socket Listener with comprehensive debugging
  useEffect(() => {
    if (!socket) {
      return;
    }

    const handleNewMessage = (messageData) => {
      console.log(
        "useChat: New message",
        messageData,
        jobId,
        receiverDetails?.userId,
      );

      const isSameJobId = messageData?.jobId === jobId; //jobApplicationId is jobId

      // Determine the correct receiver ID to compare with
      const receiverIdToCompare = recipientUserId;

      const isFromReceiver = messageData?.senderId === receiverIdToCompare;

      if (isSameJobId && isFromReceiver) {
        // console.log("✅ useChat: Message matches current chat - ADDING TO UI");
        const newMessage = {
          messageId: messageData?.chatId,
          message: messageData?.content,
          attachments: messageData?.attachments || [],
          // createdName: receiverDetails?.name,
          createdAt: messageData?.createdAt,
          seen: messageData?.seen,
          createdBy: messageData?.senderId,
          receivedBy: messageData?.recipientId,
        };
        setMessages((prev) => [...prev, newMessage]);
      }
    };

    const handleTyping = (data) => {
      console.log("Typing received", data);
      if (data?.jobId === jobId) {
        setIsTyping(data?.typing);
      }
    };

    const handleRefreshMessages = (messageData) => {
      return;
      const isSameJobId = messageData?.jobId === jobId;
      const isFromReceiver = messageData?.senderId === recipientUserId;

      if (isSameJobId && isFromReceiver) {
        refreshMessages();
      }
    };

    const handleMessageRead = (messageResponse) => {
      setMessages((prev) => {
        return prev?.map((item) => {
          if (item?.messageId === messageResponse?._id) {
            return { ...item, seen: true };
          } else {
            return item;
          }
        });
      });
    };

    // Debug socket connection state
    const handleConnect = () => {
      // console.log("✅ useChat: Socket connected", socket.id);
    };

    const handleDisconnect = (reason) => {
      // console.log("🔌 useChat: Socket disconnected", reason);
    };

    const handleConnectError = (error) => {
      // console.error("❌ useChat: Socket connection error", error.message);
    };

    // Remove existing listeners to avoid duplicates
    socket.off(MESSAGE_EVENTS.RECEIVE_MESSAGE, handleNewMessage);
    socket.off(MESSAGE_EVENTS.TYPING, handleTyping);
    socket.off(MESSAGE_EVENTS.PRESENCE, handlePresenceEvent);
    // socket.off(MESSAGE_EVENTS.REFRESH_MESSAGES, handleRefreshMessages);
    // socket.off("tempMessage", handleNewMessage);
    socket.off("messageReadByReceiver", handleMessageRead);
    socket.off("connect", handleConnect);
    socket.off("disconnect", handleDisconnect);
    socket.off("connect_error", handleConnectError);

    // Add new listeners
    // socket.on("newMessage", handleNewMessage);
    socket.on(MESSAGE_EVENTS.RECEIVE_MESSAGE, handleNewMessage);
    socket.on(MESSAGE_EVENTS.TYPING, handleTyping);
    socket.on(MESSAGE_EVENTS.PRESENCE, handlePresenceEvent);
    // socket.on(MESSAGE_EVENTS.REFRESH_MESSAGES, handleRefreshMessages);
    // if(jobId){
    //   socket
    // }
    // socket.on("tempMessage", handleNewMessage);
    socket.on("messageReadByReceiver", handleMessageRead);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);

    // console.log("✅ useChat: Socket listeners setup complete");

    // Cleanup on component unmount or dependency change
    return () => {
      // console.log("🧹 useChat: Cleaning up socket listeners");
      socket.off(MESSAGE_EVENTS.RECEIVE_MESSAGE, handleNewMessage);
      socket.off(MESSAGE_EVENTS.TYPING, handleTyping);
      socket.off(MESSAGE_EVENTS.PRESENCE, handlePresenceEvent);
      // socket.off(MESSAGE_EVENTS.REFRESH_MESSAGES, handleRefreshMessages);
      socket.off("messageReadByReceiver", handleMessageRead);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
    };
  }, [socket, jobId, recipientUserId, user?._id, dispatch]);

  // Fetching list of messages
  useEffect(() => {
    console.log("Fetching params", jobId, receiverDetails);
    if (jobId && recipientUserId) {
      refreshMessages();
    }
  }, [recipientUserId, jobId]);

  // Optimistically clear this room's unread badge the moment it is opened,
  // independent of the mark-seen API (server is persisted by the effect below).
  useEffect(() => {
    if (!fetchInbox && jobId && user?._id) {
      dispatch(resetRoomUnread({ jobId, userId: user?._id }));
    }
  }, [jobId, user?._id, fetchInbox, dispatch]);

  // Mark messages as seen
  useEffect(() => {
    if (fetchInbox) {
      // console.log("📨 useChat: Fetching inbox messages");
      dispatch(fetchJobMessagesProfile());
      return;
    }

    if (messages?.length > 0) {
      const selfId = user?._id;
      const unseenMessages = messages?.filter(
        (item) => !item?.seen && item?.createdBy !== selfId,
      );
      console.log("unseen messages", unseenMessages);
      if (unseenMessages.length > 0) {
        // console.log("👀 useChat: Marking messages as seen", {
        //   unseenMessages,
        //   count: unseenMessages.length,
        //   messageIds: unseenMessages.map((m) => m.messageId),
        // });

        const unseenMessageIds = unseenMessages
          .map((message) => message?.messageId || message?._id)
          .filter(Boolean); // Filter out temporary message IDs

        if (unseenMessageIds.length > 0) {
          updateMessageSeen({ messageIds: unseenMessageIds })
            .then(() => {
              // console.log("✅ useChat: Messages marked as seen");
              // Optimistically clear this room's badge; focus reconcile corrects drift.
              dispatch(resetRoomUnread({ jobId, userId: user?._id }));
            })
            .catch((error) => {
              // console.error(
              //   "❌ useChat: Failed to mark messages as seen",
              //   error,
              // );
            });
        }
      }
    }
  }, [messages, fetchInbox, user?._id, dispatch]);

  // console.log("💬 useChat: Current state", {
  //   messageCount: messages.length,
  //   isLoading: isMessageLoading,
  //   isSending: isMessageSending,
  //   hasSocket: !!socket,
  //   socketConnected: socket?.connected,
  // });

  return {
    isMessageLoading,
    messages,
    isMessageSending,
    sendMessage,
    appendLocalMessage,
    loadOlderMessages,
    isOlderMessageLoading,
    triggerTyping,
    isTyping,
    hasNextPage,

    onlineStatus,
  };
};

export default useChat;
